import { TypeChecker } from '@attain-apps/type-guards'

import type { Color } from './color'
import type { Scale } from './scale'

/**
 * Class used for managing the assignment of colors to each entity ID
 */
export class Palette<
  TId extends string = string,
  TColor extends Color = Color,
> {
  /**
   * Creates a {@link Palette} from one or more {@link Scale}
   */
  public static from<TId extends string, TColor extends Color>(
    scales: Maybe.ReadonlyArray<Scale<TColor>>,
    options: Omit<Palette.constructor.Params<TId, TColor>, 'colors'> = {},
  ): Palette<TId, TColor> {
    const colors = TypeChecker.isReadonlyArray(scales)
      ? scales.flatMap(scale => scale.all)
      : scales.all
    return new Palette({ ...options, colors })
  }

  /**
   * Key-value pairs associating each entity ID with its assigned color.
   */
  private _lookup: Partial<Record<TId, Color>> = {}

  /**
   * Array of colors that are not currently assigned to any entity ID.
   */
  private _unassignedColors: Color[] = []

  /**
   * Represents an array of colors intended for assignment to entity IDs.
   */
  public readonly colors: readonly TColor[]

  public get ids(): readonly TId[] {
    return Object.keys(this._lookup) as TId[]
  }

  constructor({ colors, ids = [] }: Palette.constructor.Params<TId, TColor>) {
    this.colors = colors
    this.assignColors(ids)
  }

  /**
   * Method to check if a color has been assigned to a given entity ID.
   */
  public hasColorAssignedTo(id: TId): boolean {
    return !!this.getColorAssignedTo(id)
  }

  /**
   * Find and returns color for given entity ID, from {@link Palette._lookup}
   */
  public getColorAssignedTo(id: TId): Color | undefined {
    return this._lookup[id]
  }

  /**
   * Set color from {@link Palette._unassignedColors} for each given entity ID.
   */
  public assignColors(ids: readonly TId[]): void {
    this._unassignedColors = [...this.colors]
    this._lookup = {}
    for (const id of ids) {
      this.getOrAssignColorFor(id)
    }
  }

  /**
   * Assigns a color to the entity ID
   */
  public assignColorTo(id: TId): void {
    this._lookup[id] = this._takeUnassignedColor()
  }

  /**
   * Returns the color assigned to a given entity ID if it exists.
   * Otherwise, it assigns a color to the entity ID.
   */
  public getOrAssignColorFor(id: TId): Color {
    return (
      this.getColorAssignedTo(id) ??
      (this._lookup[id] = this._takeUnassignedColor())
    )
  }

  /**
   * Removes color assignment for given entity ID.
   */
  public unassignColorFrom(id: TId): void {
    const color = this.getColorAssignedTo(id)
    if (color) {
      this._unassignedColors.push(color)
    }
    delete this._lookup[id]
  }

  /**
   * Assigns a color to a given ID while preventing duplicates across other IDs. If
   * all colors have been used, colors may be reassigned.
   */
  private _takeUnassignedColor(): Color {
    if (this._unassignedColors.length === 0) {
      this._unassignedColors = [...this.colors]
    }
    const color = this._unassignedColors.shift()
    if (color === undefined) {
      throw new Error('No available unassigned colors available.')
    }
    return color
  }
}
export namespace Palette {
  export namespace constructor {
    export interface Params<
      TId extends string = string,
      TColor extends Color = Color,
    > {
      colors: readonly TColor[]
      ids?: readonly TId[]
    }
  }
}
